import React, { useState, useEffect } from "react";
import {
  Card,
  InputNumber,
  Button,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Table,
  Image,
} from "antd";
import { SaveOutlined, CalculatorOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const FiniteSource = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);

  const [inputData, setInputData] = useState({
    name: fileName || "Untitled Analysis",
    N: 10,
    lam: 0.05,
    mu: 0.12,
  });

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (fileId) {
      fetchAnalysis();
    } else if (initialConfig) {
      setInputData((prev) => ({
        ...prev,
        name: initialConfig.name || prev.name,
      }));
    }
  }, [fileId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `/api/retrieve_finiteSource/${fileId}/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { input_data, output_data, chart_url, name } = response.data;

      const restoredInput = {
        name: name || fileName,
        N: input_data.N,
        lam: input_data.lam,
        mu: input_data.mu,
      };

      setInputData(restoredInput);
      setResultData({ output_data, chart_url });

    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to fetch analysis details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSolve = async () => {
    if (
      inputData.N === null ||
      inputData.lam === null ||
      inputData.mu === null
    ) {
      message.error("Please fill in all required inputs correctly.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: inputData.name,
        input_data: {
          N: inputData.N,
          lam: inputData.lam,
          mu: inputData.mu,
        },
      };

      let response;
      if (fileId) {
        response = await axios.put(
          `/api/update_finiteSource/${fileId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          "/api/save_finiteSource/",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Ensure the frontend updates selectedFile.id after saving
        setSelectedFile({
          id: response.data.id,
          name: response.data.name,
          type: "finite_source",
        });
      }

      setResultData({
        output_data: response.data.output_data,
        chart_url: response.data.chart_url,
      });

      message.success("Analysis saved and solved successfully");
      setActiveTab("2"); // Switch to results tab
    } catch (error) {
      console.error("Solve error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to solve analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!resultData || !resultData.output_data) return null;

    const { metrics, probability_table } = resultData.output_data;

    let tables = [];

    if (metrics) {
      const metricsData = Object.entries(metrics).map(([key, value], index) => ({
        key: 'metric_' + index,
        metric: key,
        value: typeof value === "number" ? value.toFixed(4) : value,
      }));

      const metricsColumns = [
        { title: "Metric", dataIndex: "metric", key: "metric" },
        { title: "Value", dataIndex: "value", key: "value" },
      ];

      tables.push(
        <Card title="Metrics" size="small" style={{ marginBottom: 16 }} key="metrics_table">
          <Table
            dataSource={metricsData}
            columns={metricsColumns}
            pagination={false}
            bordered
            size="small"
          />
        </Card>
      );
    }

    if (probability_table && Array.isArray(probability_table)) {
      const probColumns = [
        { title: "n", dataIndex: "n", key: "n" },
        { title: "Pn", dataIndex: "Pn", key: "Pn", render: (val) => val.toFixed(4) },
        { title: "Cumulative Pn", dataIndex: "CumulativePn", key: "CumulativePn", render: (val) => val.toFixed(4) },
      ];

      const probData = probability_table.map((row, index) => ({
        key: 'prob_' + index,
        ...row,
      }));

      tables.push(
        <Card title="Probability Table" size="small" key="prob_table">
          <Table
            dataSource={probData}
            columns={probColumns}
            pagination={false}
            bordered
            size="small"
          />
        </Card>
      );
    }

    return <div>{tables}</div>;
  };

  return (
    <Card
      title={<Title level={4}>{inputData.name}</Title>}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
        >
          {fileId ? "Update & Solve" : "Save & Solve"}
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Inputs" key="1">
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Card title="Parameters" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Population Size (N):</Text>
                  <br />
                  <InputNumber
                    value={inputData.N}
                    onChange={(val) =>
                      setInputData({ ...inputData, N: val })
                    }
                    min={1}
                    precision={0}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Arrival Rate (lam):</Text>
                  <br />
                  <InputNumber
                    value={inputData.lam}
                    onChange={(val) =>
                      setInputData({ ...inputData, lam: val })
                    }
                    min={0.0001}
                    step={0.01}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
                <div>
                  <Text strong>Service Rate (mu):</Text>
                  <br />
                  <InputNumber
                    value={inputData.mu}
                    onChange={(val) =>
                      setInputData({ ...inputData, mu: val })
                    }
                    min={0.0001}
                    step={0.01}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <Button
              type="primary"
              size="large"
              icon={<CalculatorOutlined />}
              onClick={handleSolve}
              loading={loading}
            >
              Calculate
            </Button>
          </div>
        </TabPane>

        <TabPane tab="Results" key="2" disabled={!resultData}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              {renderOutputTable()}
            </Col>
            {resultData?.chart_url && (
              <Col span={24}>
                <Card title="Chart">
                  <div style={{ textAlign: "center" }}>
                    <Image
                      src={resultData.chart_url.startsWith('http') || resultData.chart_url.startsWith('data:') ? resultData.chart_url : `http://127.0.0.1:8000${resultData.chart_url}`}
                      alt="Result Chart"
                      fallback="https://via.placeholder.com/600x400?text=Chart+Not+Available"
                      style={{ maxWidth: "100%", maxHeight: "500px" }}
                    />
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default FiniteSource;
